import React from 'react';

import { Box, Theme } from '@mui/material';

import { Nullable } from '@clearverify/types';
import { formatMoney, numberWithCommas, sum } from '@clearverify/utils';

import {
  DashboardReportAndRevenueResponseReport,
  DashboardReportAndRevenueResponseRevenue,
  LastUploadRolesResponse,
} from 'src/api';
import { useCurrentUserEmployer } from 'src/hooks/employer';
import { useCurrentUserPeo } from 'src/hooks/peo';
import { useCurrentUserPeoPayrollProvider } from 'src/hooks/peoPayrollProvider';
import { SxProp } from 'src/types/mui';
import { formatLastUploadDate, formatLastUploadRecords } from 'src/utils/dashboard';

import { DashboardCard } from './DashboardCard';

export type DashboardReportAndRevenueBlockProps = Partial<
  Pick<
    DashboardReportAndRevenueResponseReport,
    | 'activeRecordAmount'
    | 'inactiveRecordAmount'
    | 'resolvedDataDisputeAmount'
    | 'pendingDataDisputeAmount'
  >
> &
  Pick<
    DashboardReportAndRevenueResponseRevenue,
    'revenueSharePayedAmount' | 'revenueShareOwedAmount'
  > & {
    loading?: boolean;
    hideRevenueShare?: boolean;
    lastUploadRoles?: Nullable<Pick<LastUploadRolesResponse, 'rolesCount' | 'uploadedAt'>>;
  };

export const DashboardReportAndRevenueBlock: React.FC<DashboardReportAndRevenueBlockProps> = ({
  activeRecordAmount = 0,
  inactiveRecordAmount = 0,
  pendingDataDisputeAmount = 0,
  resolvedDataDisputeAmount = 0,
  revenueSharePayedAmount,
  revenueShareOwedAmount,
  loading,
  hideRevenueShare = false,
  lastUploadRoles,
}) => {
  const { peo } = useCurrentUserPeo();
  const { peoPayrollProvider } = useCurrentUserPeoPayrollProvider();
  const { employer } = useCurrentUserEmployer();

  const recordAmount = numberWithCommas(sum(activeRecordAmount, inactiveRecordAmount));

  const dataDisputeAmount = numberWithCommas(
    sum(resolvedDataDisputeAmount, pendingDataDisputeAmount),
  );

  const isRevenueShareEnabled =
    !hideRevenueShare &&
    (employer?.revenueShareSetting?.id ||
      peo?.revenueShareSetting?.id ||
      peoPayrollProvider?.revenueShareSetting?.id);

  const columnCount = (isRevenueShareEnabled ? 4 : 3) + (lastUploadRoles ? 1 : 0);

  return (
    <Box
      sx={dashboardReportAndRevenueBlockBoxStyles}
      gridTemplateColumns={`repeat(${columnCount}, 1fr)`}
    >
      <DashboardCard
        title="Records on the platform"
        firstValue={`${numberWithCommas(activeRecordAmount ?? 0)} Active`}
        secondValue={`${numberWithCommas(inactiveRecordAmount ?? 0)} Inactive`}
        value={recordAmount}
        loading={loading}
      />
      <DashboardCard
        title="Last Month Data Disputes"
        firstValue={`${numberWithCommas(resolvedDataDisputeAmount ?? 0)} Resolved`}
        secondValue={`${numberWithCommas(pendingDataDisputeAmount ?? 0)} In Progress`}
        value={dataDisputeAmount}
        secondColor={(theme: Theme) => theme.palette.warning.main}
        loading={loading}
      />
      {/* Hidden entirely until MDM reports a successful load for this org. */}
      {lastUploadRoles ? (
        <DashboardCard
          title="Latest data received"
          value={formatLastUploadDate(lastUploadRoles.uploadedAt)}
          description={formatLastUploadRecords(lastUploadRoles.rolesCount)}
          loading={loading}
        />
      ) : null}
      {isRevenueShareEnabled ? (
        <DashboardCard
          title="Revenue Share"
          description={`Paid: ${formatMoney(revenueSharePayedAmount)} Pending: ${formatMoney(revenueShareOwedAmount)}`}
          value={formatMoney(sum(revenueSharePayedAmount, revenueShareOwedAmount))}
          loading={loading}
        />
      ) : null}
    </Box>
  );
};

const dashboardReportAndRevenueBlockBoxStyles: SxProp = theme => ({
  display: 'grid',
  mt: 4,
  gap: 2,
  overflow: 'auto',

  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },

  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'none',
  },
});
